/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import EntryActions from 'actions/EntryActions';
// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants, {NOTEBOOK_ITEMS} from 'constants/ModelTypeConstants';
import {getRegistrySupportedTypes} from 'constants/RegistryConstants';
import ExportActions from 'exports/ExportActions';
import FolderSelector from 'file-browser/FolderSelector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import openAICheckerPanel from 'notebook/ai-tools/openAICheckerPanel';
import ReviewButtonGroup from 'notebook/reviews/ReviewButtonGroup';
import {Review, ReviewSummary} from 'notebook/reviews/ReviewConstants';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {ENTRY_EXPORT_STARTED_EVENT, ENTRY_EXPORT_FINISHED_EVENT} from 'performance/processNotebookEvents';
import * as DeleteUtil from 'permissions/DeleteUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import UpgradeAccountModal, {ENTERPRISE_FEATURES_KEYS} from 'start/upgrade-account/UpgradeAccountModal';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import fuzzydate from 'util/fuzzydate';
import {OngoingTaskResponse} from 'util/TaskRunner';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';

import {
  EntryReviewLifecycleQuery,
  EntryReviewLifecycleQueryVariables,
} from './__generated/EntryReviewLifecycleQuery';
import InformationPanelNameInput from './InformationPanelNameInput';

class InformationSubpanelConnector extends DataConnector<InformationSubpanelProps, EnhancedProps> {
  override entityStore: any;
  override shouldSubscribeToRedux = false;
  override stores = [EntityStore];

  enhanceProps(): EnhancedProps {
    const currentUser = this.entityStore().currentUser();
    return {userMetadata: currentUser ? currentUser.metadata : null};
  }
}

export const ENTRY_REVIEW_LIFECYCLE_QUERY = gql`
  query EntryReviewLifecycleQuery($entryId: StrictString!) @skipAudit {
    entry(id: $entryId) {
      ... on EntryNode {
        id
        review {
          id
          status
          reviewLifecycleVersion {
            id
          }
        }
        project {
          ... on ProjectNode {
            id
            reviewLifecycles {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface InformationSubpanelProps {
  additionalPanelContent?: React.ReactNode;
  archiveRecord?: any; // TODO: ArchiveConstants.ARCHIVE_RECORD_SHAPE
  currentTagSchema?: any;
  deleteModel?: (...args: Array<any>) => any;
  fileId: string;
  fileType: string;
  folder?: StringIDFolder;
  friendlyFileType?: string;
  getDownloadInfo?: (...args: Array<any>) => any;
  name: string;
  onClose: () => void;
  permissions: Permissions;
  requestExport?: (...args: Array<any>) => any;
  review?: Review;
  saveChangesToModel: (name: string | null, folderId: string | null, tagSchemaId: string | null) => any;
  showPatentsafeExport: boolean;
  thirdPartyStorageServiceData?: {
    isSendingToPatentsafe?: boolean;
    lastSentTimestamp?: string;
    lastSentUser?: {
      handle: string;
    };
  };
}

interface EnhancedProps {
  userMetadata?: any;
}

type State = any;

interface ReviewButtonsProps {
  fileId: string;
  fileType: string;
  review?: Review;
  entryReviewLifecycleInfo?: {
    projectUsesReviewLifecycle: boolean;
    wasLastSentForLegacyReview: boolean;
  };
}

interface ReviewButtonSectionProps extends ReviewButtonsProps {}

async function onReviewUpdate({
  fileId,
  fileType,
  review,
  permissions,
}: {
  fileId: string;
  fileType: string;
  review: ReviewSummary;
  permissions: Permissions;
}): Promise<void> {
  if (fileType === ModelTypeConstants.ENTRY) {
    if (BenchlingConfig.ENABLE_FROZEN_CHIPS) {
      await ReviewUtil.refreshFrozenChipVersion(fileId, review.status);
    } else {
      EntryActions.postServerUpdate(fileId, {review, permissions});
    }
  } else {
    assert(fileType === ModelTypeConstants.STAGE_ENTRY, 'unexpected fileType');
    StageEntryActions.updateReview(fileId, review, permissions);
  }
}

// Only render divider if there are review buttons to render
function ReviewButtonGroupWrapper({children}: {children: JSX.Element | null}): JSX.Element {
  return children != null ? (
    <>
      <Row>
        <Col sm={12}>{children}</Col>
      </Row>
      <div className="panel-divider" />
    </>
  ) : (
    <>{children}</>
  );
}

function ReviewButtonSection({
  fileId,
  fileType,
  review,
  entryReviewLifecycleInfo,
}: ReviewButtonSectionProps): JSX.Element | null {
  return (
    // @ts-expect-error ts-migrate(2604) FIXME: JSX element type 'ReviewButtonGroup' does not have... Remove this comment to see the full error message
    <ReviewButtonGroup
      fileId={fileId}
      review={review}
      fullWidth={true}
      isVertical={true}
      onUpdate={(
        fileId: string,
        review: ReviewSummary,
        filePermissions: Permissions,
        _snapshotTask: OngoingTaskResponse | null
      ) => {
        onReviewUpdate({fileId, fileType, review, permissions: filePermissions});
      }}
      size={ButtonSize.SMALL}
      className="informationSubpanel-reviewButtonGroup"
      entryReviewLifecycleInfo={entryReviewLifecycleInfo}
      wrapperComponent={ReviewButtonGroupWrapper}
    />
  );
}

function ReviewButtonsForEntry({fileId, fileType, review}: ReviewButtonsProps): JSX.Element | null {
  return (
    <BenchlingQuery<EntryReviewLifecycleQuery, EntryReviewLifecycleQueryVariables>
      query={ENTRY_REVIEW_LIFECYCLE_QUERY}
      variables={{entryId: fileId}}
      renderLoading={() => (
        <>
          <div>
            <CenteredHorizontalThrobber suppressPadding={true} className={css(styles.customPaddingTop)} />
          </div>
          <div className="panel-divider" />
        </>
      )}
    >
      {({entry}) => {
        if (isInaccessible(entry)) {
          return null;
        }
        return (
          <ReviewButtonSection
            fileId={fileId}
            fileType={fileType}
            review={review}
            entryReviewLifecycleInfo={{
              projectUsesReviewLifecycle: entry.project.reviewLifecycles.length > 0,
              wasLastSentForLegacyReview: entry.review != null && entry.review.reviewLifecycleVersion == null,
            }}
          />
        );
      }}
    </BenchlingQuery>
  );
}

function ReviewButtons({fileId, fileType, review}: ReviewButtonsProps): JSX.Element | null {
  if (!ReviewUtil.isTypeReviewable(fileType)) {
    return null;
  }
  if (ReviewUtil.canUserReviewType(fileType)) {
    if (fileType === ModelTypeConstants.ENTRY) {
      return <ReviewButtonsForEntry fileId={fileId} fileType={fileType} review={review} />;
    }

    return <ReviewButtonSection fileId={fileId} fileType={fileType} review={review} />;
  }

  if (UpgradeAccountModal.isEnabled()) {
    return (
      <Row>
        <Col md={12}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            fullWidth={true}
            icon="star"
            onClick={() => {
              UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.REVIEWS);
            }}
            size={ButtonSize.SMALL}
            text="Send for review"
          />
        </Col>
      </Row>
    );
  }
  return null;
}

export class InformationSubpanel extends React.Component<InformationSubpanelProps & EnhancedProps, State> {
  static defaultProps = {
    showPatentsafeExport: false,
  };

  constructor(props: any, context: any) {
    super(props, context);
    const downloadInfo = this._getDownloadInfo();
    this.state = {
      name: props.name,
      folderId: this._getFolderId(),
      selectedDownloadInfoIndex: downloadInfo && Array.isArray(downloadInfo) ? 0 : null,
    };
  }

  _getFolderId = (props?: any): string | null => {
    if (!props) {
      props = this.props;
    }
    return props.folder ? props.folder.api_identifier : null;
  };
  _handleChangeFolderId = (e: any): void => {
    this.setState({folderId: e.target.value});
  };

  _isRegistered = (): boolean =>
    this.props.folder != null && RegistryUtil.isRegistry(this.props.folder.source);

  _getPartialFeatureDefinition = (): {
    item: {id: string; permissions: Permissions};
    itemState: {isRegistered: boolean};
  } => ({
    item: {id: this.props.fileId, permissions: this.props.permissions},
    itemState: {isRegistered: this._isRegistered()},
  });

  override componentDidMount(): void {
    if (!this.props.userMetadata && EntityStore.currentUser() != null) {
      UserActions.loadMetadata();
    }
  }

  override UNSAFE_componentWillReceiveProps(nextProps: InformationSubpanelProps): void {
    if (nextProps.name !== this.props.name) {
      this.setState({name: nextProps.name});
    }
    if (this._getFolderId(nextProps) !== this._getFolderId()) {
      this.setState({folderId: this._getFolderId(nextProps)});
    }
  }

  saveChangesToModel = async (): Promise<void> => {
    const {folderId, tagSchemaId} = RegistryUtil.parseFolderValue(this.state.folderId);
    await this.props.saveChangesToModel(this.state.name, folderId, tagSchemaId);
  };

  showContainingFolder = (): void => {
    const section = Array.from(NOTEBOOK_ITEMS).includes(this.props.fileType as ModelTypeConstants)
      ? FileBrowserConstants.NOTEBOOK_SECTION
      : FileBrowserConstants.INVENTORY_SECTION;

    assert(this.props.folder != null, 'Folder cannot be null when showing containing folder.');
    listFolderSection(this.props.folder, section);
  };

  submitHandler = async (e: any): Promise<void> => {
    e.preventDefault();
    if (this.props.permissions.writable) {
      await this.saveChangesToModel();
    }
  };
  _hasEdits = (): boolean =>
    this._getFolderId() !== this.state.folderId || this.props.name !== this.state.name;
  _requestPatentsafeExport = (): void => {
    ExportActions.requestPatentsafeExport(this.props.fileId);
  };
  _exportAuditLogs = (): void => {
    AuditActions.exportAuditLogs([this.props.fileId]);
  };
  _getPatentSafeMessage = (): string => {
    if (this.props.thirdPartyStorageServiceData!.isSendingToPatentsafe) {
      return 'Sending to PatentSafe...';
    } else {
      return 'Send to PatentSafe';
    }
  };
  _renderPatentsafeExportButton = (): JSX.Element => (
    <Row>
      <Col md={12}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          // Add a unique class for testing ID purposes
          className="patentsafeExportButton"
          disabled={this.props.thirdPartyStorageServiceData!.isSendingToPatentsafe}
          fullWidth={true}
          onClick={this._requestPatentsafeExport}
          size={ButtonSize.SMALL}
          text={this._getPatentSafeMessage()}
        />
        {(() => {
          if (this.props.thirdPartyStorageServiceData!.lastSentTimestamp != null) {
            const user = this.props.thirdPartyStorageServiceData!.lastSentUser;
            const fuzzyTimestamp = fuzzydate(this.props.thirdPartyStorageServiceData!.lastSentTimestamp);
            const lastSentMsg = `Last sent ${
              user != null ? `by ${getPartyDisplayString(user)} ` : ''
            }${fuzzyTimestamp}`;
            return (
              <div className="patentsafeLastSentMsg faded text-center u-tiny-spacing-top">{lastSentMsg}</div>
            );
          }
          return null;
        })()}
      </Col>
    </Row>
  );

  _renderAINotebookCheckerButton(): JSX.Element | null {
    if (!BenchlingConfig.ELN_ENABLE_AI_CHECKER || this.props.fileType !== ModelTypeConstants.ENTRY) {
      return null;
    }
    return (
      <Row>
        <Col md={12}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            fullWidth={true}
            icon="magnifying-glass"
            onClick={() => {
              openAICheckerPanel(this.props.fileId, 'information-panel');
              this.props.onClose();
            }}
            size={ButtonSize.SMALL}
            text="Check entry"
          />
        </Col>
      </Row>
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  _getDownloadInfo = () =>
    typeof this.props.getDownloadInfo === 'function' ? this.props.getDownloadInfo() : undefined;
  _renderDownloadButtonLink = (url: any, title: any): JSX.Element => (
    <Row>
      <Col md={12}>
        <ButtonLink
          buttonStyle={ButtonStyle.SECONDARY}
          href={url}
          fullWidth={true}
          size={ButtonSize.SMALL}
          text={title}
          dataTestAttrs={{component: 'InformationSubpanel', element: 'downloadButton'}}
        />
        {isFeatureEnabled({feature: StandardFeature.EDIT, ...this._getPartialFeatureDefinition()}) && (
          <div className="panel-divider" />
        )}
      </Col>
    </Row>
  );
  _renderDownloadButtons = (): JSX.Element | null => {
    const downloadInfo = this._getDownloadInfo();
    if (!downloadInfo) {
      return null;
    }
    if (Array.isArray(downloadInfo)) {
      const selectedDownloadInfo = downloadInfo[this.state.selectedDownloadInfoIndex];
      return (
        <>
          <Row className={css(styles.centerVertically)}>
            <Col md={6}>Export Data</Col>
            <Col md={6}>
              <select
                value={this.state.selectedDownloadInfoIndex}
                onChange={(e) => {
                  this.setState({selectedDownloadInfoIndex: e.target.value});
                }}
                {...getTestProps({component: 'InformationSubpanel', element: 'exportFormatDropdown'})}
              >
                {downloadInfo.map((info, index) => (
                  <option value={index} key={index}>
                    {info.title}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
          {this._renderDownloadButtonLink(selectedDownloadInfo.url, 'Download')}
        </>
      );
    } else {
      return this._renderDownloadButtonLink(downloadInfo.url, downloadInfo.title);
    }
  };
  _handleChangeName = (e: any): void => {
    this.setState({name: e.target.value});
  };

  override render(): JSX.Element {
    const {readable} = this.props.permissions;
    const writable = isFeatureEnabled({
      feature: StandardFeature.EDIT,
      ...this._getPartialFeatureDefinition(),
    });
    const movable = isFeatureEnabled({feature: StandardFeature.MOVE, ...this._getPartialFeatureDefinition()});
    const deletable = isFeatureEnabled({
      feature: StandardFeature.DELETE,
      ...this._getPartialFeatureDefinition(),
    });
    const displayExportFileButton = EntityStore.currentUser() != null && this.props.requestExport != null;
    const isEntry = this.props.friendlyFileType === 'entry';
    return (
      <div {...getTestProps({component: 'InformationSubpanel', element: 'root'})}>
        <form onSubmit={this.submitHandler}>
          <div className="form-group u-small-spacing-top">
            <Row>
              <Col md={4}>
                <h5>Name</h5>
              </Col>
              <Col md={8}>
                <InformationPanelNameInput
                  archiveRecord={this.props.archiveRecord}
                  id={this.props.fileId}
                  isWritable={writable}
                  name={this.state.name}
                  onChange={this._handleChangeName}
                />
              </Col>
            </Row>
            {
              // If the item was accessed via a share-link, the folder selector will fail to fetch folders,
              // so we don't show the selector. Technically, we could thread down BaseFolderItem.folder, but
              // that would require some refactoring.
              readable && this.props.folder != null && !RegistryUtil.isRegistry(this.props.folder.source) && (
                <Row>
                  <Col md={4}>
                    <h5>
                      Folder
                      <Tooltip title="Show containing folder" container="body">
                        <a className="u-tiny-spacing-left" onClick={this.showContainingFolder}>
                          <BenchlingIcon icon="upload" />
                        </a>
                      </Tooltip>
                    </h5>
                  </Col>
                  <Col md={8}>
                    <FolderSelector
                      currentTagSchema={this.props.currentTagSchema}
                      readOnly={!movable}
                      showInventory={Array.from(getRegistrySupportedTypes() as Array<string>).includes(
                        this.props.fileType
                      )}
                      showNotebook={Array.from(NOTEBOOK_ITEMS).includes(
                        this.props.fileType as ModelTypeConstants
                      )}
                      value={this.state.folderId}
                      onChange={this._handleChangeFolderId}
                      unsetOnFetchFail={true}
                      checkExistingItemForDataModsMovement={true}
                    />
                  </Col>
                </Row>
              )
            }
          </div>
          <div>
            {(writable || movable) && (
              <Row>
                <Col md={12}>
                  <Button
                    buttonStyle={ButtonStyle.SECONDARY}
                    disabled={!this._hasEdits()}
                    fullWidth={true}
                    onClick={this.saveChangesToModel}
                    size={ButtonSize.SMALL}
                    text="Update information"
                    type="button"
                  />
                </Col>
              </Row>
            )}
          </div>
        </form>
        <div className="panel-divider" />
        <ReviewButtons fileId={this.props.fileId} fileType={this.props.fileType} review={this.props.review} />
        {this._renderAINotebookCheckerButton()}
        {this._renderDownloadButtons()}
        {BenchlingConfig.ENABLE_DELETE && this.props.deleteModel != null && (
          <Row>
            <Col md={12}>
              <Tooltip
                title={
                  deletable
                    ? 'Delete'
                    : DeleteUtil.getUndeletableTooltipTitle({
                        isArchived: isArchived({
                          id: this.props.fileId,
                          name: this.props.name,
                          archiveRecord: this.props.archiveRecord,
                        }),
                      })
                }
              >
                <Button
                  buttonStyle={ButtonStyle.NEGATIVE}
                  disabled={!deletable}
                  fullWidth={true}
                  onClick={this.props.deleteModel}
                  size={ButtonSize.SMALL}
                  text={`Delete ${this.props.friendlyFileType}`}
                />
              </Tooltip>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={12}>
            <ArchiveOrUnarchiveButton
              fullWidth={true}
              isArchivable={
                isFeatureEnabled({
                  feature: StandardFeature.ARCHIVE,
                  ...this._getPartialFeatureDefinition(),
                }) &&
                (!APIUtil.isMolecule(this.props.fileId) || BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE)
              }
              isUnarchivable={isFeatureEnabled({
                feature: StandardFeature.UNARCHIVE,
                ...this._getPartialFeatureDefinition(),
              })}
              item={{
                id: this.props.fileId,
                name: this.props.name,
                archiveRecord: this.props.archiveRecord,
              }}
              onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
              onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
              showText={true}
            />
          </Col>
        </Row>

        {displayExportFileButton || (this.props.showPatentsafeExport && <div className="panel-divider" />)}
        {displayExportFileButton && (
          <Row>
            <Col md={12}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="__exportFileButton"
                fullWidth={true}
                onClick={async () => {
                  logProfilerTimingEvent(ENTRY_EXPORT_STARTED_EVENT, {
                    fileId: this.props.fileId,
                    exportType: 'file',
                  });
                  await this.props.requestExport?.();
                  logProfilerTimingEvent(ENTRY_EXPORT_FINISHED_EVENT, {
                    fileId: this.props.fileId,
                    exportType: 'file',
                  });
                }}
                size={ButtonSize.SMALL}
                text={`Export ${this.props.friendlyFileType}`}
                dataTestAttrs={{component: 'InformationSubpanel', element: 'exportFileButton'}}
              />
            </Col>
          </Row>
        )}
        {this.props.showPatentsafeExport && this._renderPatentsafeExportButton()}
        {this.props.additionalPanelContent}
        {isEntry && (
          <Row>
            <Col md={12}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                fullWidth={true}
                onClick={this._exportAuditLogs}
                size={ButtonSize.SMALL}
                text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
                dataTestAttrs={{component: 'InformationSubpanel', element: 'exportAuditLogButton'}}
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }
}

export default InformationSubpanelConnector.connect(InformationSubpanel);
const styles = StyleSheet.create({
  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },
  customPaddingTop: {
    paddingTop: '5px',
  },
});
